import { CommonModule } from "@angular/common";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatChipsModule } from "@angular/material/chips";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatSelectModule } from "@angular/material/select";
import {
  DynamicCsvReportSearchParams,
  GenerateReportService,
} from "@shared/services/generate-report.service";
import { CustomHeaderService } from "app/layout/common/custom-header-title.service";
import { BranchDto } from "app/modules/masters/Branch/branch.dto";
import { BranchService } from "app/modules/masters/Branch/branch.service";
import { EntityStageConfigurationDto } from "app/modules/masters/entity-stage-configuration/entity-stage-configuration.dto";
import { EntityStageConfigurationService } from "app/modules/masters/entity-stage-configuration/entity-stage-configuration.service";
import { ProductCategoryDto } from "app/modules/masters/product-category/product-category.dto";
import { ProductCategoryService } from "app/modules/masters/product-category/product-category.service";
import { DateTime } from "luxon";

type DownloadStatus = "idle" | "working" | "done";

interface ReportFilterControls {
  readonly branchSetCode: FormControl<string>;
  readonly stage: FormControl<string>;
  readonly createdDateFrom: FormControl<DateTime | null>;
  readonly createdDateTo: FormControl<DateTime | null>;
}

interface FilledFilter {
  readonly label: string;
  readonly value: string;
}

@Component({
  selector: "app-dynamic-csv-report",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatChipsModule,
  ],
  templateUrl: "./dynamic-csv-report.component.html",
  styleUrl: "./dynamic-csv-report.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DynamicCsvReportComponent implements OnInit, OnDestroy {
  private readonly reportService = inject(GenerateReportService);
  private readonly customHeaderService = inject(CustomHeaderService);
  private readonly productCategoryService = inject(ProductCategoryService);
  private readonly branchService = inject(BranchService);
  private readonly entityStageConfigurationService = inject(
    EntityStageConfigurationService,
  );
  private readonly destroyRef = inject(DestroyRef);

  readonly reportCode = "loan-pipeline-report";
  readonly today = DateTime.now().startOf("day");
  readonly productCategories = signal<readonly ProductCategoryDto[]>([]);
  readonly branches = signal<readonly BranchDto[]>([]);
  readonly stages = signal<readonly EntityStageConfigurationDto[]>([]);
  readonly selectedProductCategoryCode = signal<string>("");
  readonly productCategoriesLoading = signal<boolean>(false);
  readonly branchesLoading = signal<boolean>(false);
  readonly stagesLoading = signal<boolean>(false);
  readonly filterLoadMessage = signal<string | null>(null);
  readonly filledFilters = signal<readonly FilledFilter[]>([]);
  readonly errorMessage = signal<string | null>(null);
  readonly status = signal<DownloadStatus>("idle");
  readonly downloading = computed(() => this.status() === "working");

  readonly filterForm = new FormGroup<ReportFilterControls>({
    branchSetCode: new FormControl("", { nonNullable: true }),
    stage: new FormControl("", { nonNullable: true }),
    createdDateFrom: new FormControl<DateTime | null>(null),
    createdDateTo: new FormControl<DateTime | null>(null),
  });

  private doneTimer?: ReturnType<typeof setTimeout>;
  private stageRequestId = 0;

  ngOnInit(): void {
    this.customHeaderService.setTitle("Report");
    this.customHeaderService.hideProductCategory();
    this.loadProductCategories();
    this.loadBranches();
  }

  ngOnDestroy(): void {
    clearTimeout(this.doneTimer);
  }

  onProductCategoryChange(event: Event): void {
    const productCategoryCode =
      (event.target as HTMLSelectElement | null)?.value ?? "";
    if (productCategoryCode === this.selectedProductCategoryCode()) {
      return;
    }

    this.selectedProductCategoryCode.set(productCategoryCode);
    this.productCategoryService.publishProductCategory(productCategoryCode);
    this.filterForm.controls.stage.setValue("");
    this.filledFilters.set([]);
    this.resetDownloadStatus();

    if (productCategoryCode) {
      this.loadStages(productCategoryCode);
    } else {
      this.stages.set([]);
    }
  }

  clearFilters(): void {
    this.filterForm.reset({
      branchSetCode: "",
      stage: "",
      createdDateFrom: null,
      createdDateTo: null,
    });
    this.filledFilters.set([]);
    this.errorMessage.set(null);
    this.resetDownloadStatus();
  }

  download(): void {
    const productCategoryCode = this.selectedProductCategoryCode();
    if (!productCategoryCode || this.downloading()) {
      return;
    }

    const dateRangeError = this.validateDateRange();
    if (dateRangeError) {
      this.errorMessage.set(dateRangeError);
      this.status.set("idle");
      return;
    }

    const searchParams = this.buildSearchParams();
    this.filledFilters.set(this.toFilledFilters());
    this.errorMessage.set(null);
    this.status.set("working");

    this.reportService
      .downloadDynamicCsvReport(
        productCategoryCode,
        this.reportCode,
        searchParams,
      )
      .subscribe({
        next: (response) => {
          this.saveFile(response);
          this.flagDone();
        },
        error: (error: HttpErrorResponse) => {
          this.errorMessage.set(
            error.status === 0
              ? "Unable to reach the server. Please try again."
              : "CSV report could not be generated. Please try again.",
          );
          this.status.set("idle");
        },
      });
  }

  branchOptionLabel(branch: BranchDto): string {
    return branch.name ? `${branch.name} (${branch.code})` : branch.code;
  }

  private loadProductCategories(): void {
    this.productCategoriesLoading.set(true);
    this.errorMessage.set(null);

    this.productCategoryService
      .getLoggedInUserProductCategory()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (productCategories) => {
          const availableProductCategories = productCategories ?? [];
          const selectedProductCategoryCode =
            this.resolveSelectedProductCategory(availableProductCategories);

          this.productCategories.set(availableProductCategories);
          this.selectedProductCategoryCode.set(selectedProductCategoryCode);
          this.productCategoriesLoading.set(false);

          if (selectedProductCategoryCode) {
            this.productCategoryService.publishProductCategory(
              selectedProductCategoryCode,
            );
            this.loadStages(selectedProductCategoryCode);
          } else {
            this.filterLoadMessage.set(
              "No product category is available for CSV report download.",
            );
          }
        },
        error: () => {
          this.productCategoriesLoading.set(false);
          this.errorMessage.set(
            "Product categories could not be loaded. Please try again.",
          );
        },
      });
  }

  private loadBranches(): void {
    this.branchesLoading.set(true);

    this.branchService
      .findRecords("id,asc", 0, 1000)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.branches.set(response.data ?? []);
          this.branchesLoading.set(false);
        },
        error: () => {
          this.branches.set([]);
          this.branchesLoading.set(false);
          this.filterLoadMessage.set(
            "Branches could not be loaded. You can still download using other filters.",
          );
        },
      });
  }

  private loadStages(productCategoryCode: string): void {
    const requestId = ++this.stageRequestId;
    this.stagesLoading.set(true);
    this.stages.set([]);
    this.filterLoadMessage.set(null);

    this.entityStageConfigurationService
      .getEntityStageByProductCategory(productCategoryCode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (stages) => {
          if (requestId !== this.stageRequestId) {
            return;
          }
          this.stages.set(this.sortStages(stages ?? []));
          this.stagesLoading.set(false);
        },
        error: () => {
          if (requestId !== this.stageRequestId) {
            return;
          }
          this.stages.set([]);
          this.stagesLoading.set(false);
          this.filterLoadMessage.set(
            "Stages could not be loaded for the selected product category.",
          );
        },
      });
  }

  private resolveSelectedProductCategory(
    productCategories: readonly ProductCategoryDto[],
  ): string {
    const currentProductCategoryCode =
      this.productCategoryService.getSelectedProductCategoryCode();
    if (
      productCategories.some(
        (productCategory) =>
          productCategory.code === currentProductCategoryCode,
      )
    ) {
      return currentProductCategoryCode;
    }
    return productCategories[0]?.code ?? "";
  }

  private sortStages(
    stages: readonly EntityStageConfigurationDto[],
  ): EntityStageConfigurationDto[] {
    return [...stages].sort((first, second) => first.order - second.order);
  }

  private validateDateRange(): string | null {
    const fromDate = this.selectedDate("createdDateFrom");
    const toDate = this.selectedDate("createdDateTo");

    if (fromDate && toDate && toDate.startOf("day") < fromDate.startOf("day")) {
      return "Created From date must be on or before Created To date.";
    }

    return null;
  }

  private buildSearchParams(): DynamicCsvReportSearchParams {
    const { branchSetCode, stage } = this.filterForm.getRawValue();
    const searchParams: Record<string, string | readonly string[]> = {};
    const trimmedBranchSetCode = branchSetCode.trim();
    const trimmedStage = stage.trim();
    const createdDateFilters = this.createdDateFilters();

    if (trimmedBranchSetCode) {
      searchParams["branchSetCode"] = `eq:${trimmedBranchSetCode}`;
    }
    if (trimmedStage) {
      searchParams["stage"] = `eq:${trimmedStage}`;
    }
    if (createdDateFilters.length > 0) {
      searchParams["createdDate"] = createdDateFilters;
    }

    return searchParams;
  }

  private createdDateFilters(): string[] {
    const fromDate = this.selectedDate("createdDateFrom");
    const toDate = this.selectedDate("createdDateTo");
    const filters: string[] = [];

    if (fromDate) {
      filters.push(`gte:${fromDate.toISODate()}T00:00:00`);
    }
    if (toDate) {
      filters.push(`lte:${toDate.toISODate()}T23:59:59`);
    }

    return filters;
  }

  private toFilledFilters(): FilledFilter[] {
    const { branchSetCode, stage } = this.filterForm.getRawValue();
    const fromDate = this.selectedDate("createdDateFrom");
    const toDate = this.selectedDate("createdDateTo");
    const filledFilters: FilledFilter[] = [];

    if (branchSetCode.trim()) {
      filledFilters.push({
        label: "Branch",
        value: this.branchFilterLabel(branchSetCode.trim()),
      });
    }
    if (stage.trim()) {
      filledFilters.push({ label: "Stage", value: stage.trim() });
    }
    if (fromDate) {
      filledFilters.push({
        label: "Created From",
        value: this.prettyDate(fromDate),
      });
    }
    if (toDate) {
      filledFilters.push({
        label: "Created To",
        value: this.prettyDate(toDate),
      });
    }

    return filledFilters;
  }

  private selectedDate(
    controlName: "createdDateFrom" | "createdDateTo",
  ): DateTime | null {
    const value = this.filterForm.controls[controlName].value;
    return value && DateTime.isDateTime(value) && value.isValid ? value : null;
  }

  private branchFilterLabel(branchSetCode: string): string {
    const branch = this.branches().find(
      (option) => option.code === branchSetCode,
    );
    return branch ? this.branchOptionLabel(branch) : branchSetCode;
  }

  private prettyDate(date: DateTime): string {
    return date.toFormat("dd LLL yyyy");
  }

  private flagDone(): void {
    this.status.set("done");
    clearTimeout(this.doneTimer);
    this.doneTimer = setTimeout(() => this.resetDownloadStatus(), 2600);
  }

  private resetDownloadStatus(): void {
    clearTimeout(this.doneTimer);
    this.status.set("idle");
  }

  private saveFile(response: HttpResponse<Blob>): void {
    const blob = response.body as Blob;
    const blobUrl = URL.createObjectURL(blob);

    const anchor = document.createElement("a");
    anchor.href = blobUrl;
    anchor.download = this.resolveFileName(response);
    anchor.click();

    URL.revokeObjectURL(blobUrl);
  }

  private resolveFileName(response: HttpResponse<Blob>): string {
    const contentDisposition = response.headers.get("Content-Disposition");
    const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
    return match?.[1] ?? `${this.reportCode}.csv`;
  }
}
